import {
  ArrowForward,
  Close,
  Done,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Person,
  VFlex,
  VIconButton,
  VStack,
  VStatusBadge,
  VideoLabel,
  new__cn as cn,
  useFlag,
} from '@/package';
import { useAuthStore } from '@/store';
import type { FrameData } from '@/type';

import type { LinkedFrameItemData, LinkedFrameListProps } from './type';
import {
  getStatusBadgeVariant,
  getStatusText,
  isApplicableFrame,
  isAssignedFrame,
} from './util';

const LinkedFrameItem = ({
  frameItem,
  isApplicable,
  isAssigned,
  onApply,
  onNavigate,
  onRemove,
}: {
  frameItem: LinkedFrameItemData;
  isApplicable: boolean;
  isAssigned: boolean;
  onApply?: () => void;
  onNavigate?: () => void;
  onRemove?: () => void;
}) => {
  const isOwnedByOther = !isAssigned && isApplicable;

  return (
    <VFlex items="center" gap={0.25} className="w-full pl-4 [&_svg]:size-4">
      <VFlex
        items="center"
        gap={0.25}
        className="h-[26px] flex-1 bg-white px-1 py-0.5"
      >
        <VFlex items="center" gap={0.25} className="flex-1">
          <VideoLabel
            className={cn({
              'text-primary-500': isApplicable,
              'text-gray-400': !isApplicable,
            })}
          />
          <span className="text-body-md text-black">{frameItem.frameSeq}</span>
        </VFlex>
      </VFlex>

      {isAssigned && isApplicable && (
        <VFlex items="center" justify="between" gap={0.25} className="w-fit">
          <VIconButton
            Icon={ArrowForward}
            size="sm"
            variant="roundPrimary"
            onClick={onNavigate}
          />
          <VIconButton
            Icon={Done}
            size="sm"
            variant="primary"
            onClick={onApply}
          />
          <VIconButton
            Icon={Close}
            size="sm"
            variant="roundDanger"
            onClick={onRemove}
          />
        </VFlex>
      )}

      {isAssigned && !isApplicable && (
        <VFlex className="w-[72px]" items="center" justify="center">
          <VStatusBadge
            size="sm"
            variant={getStatusBadgeVariant(frameItem.workState)}
          >
            {getStatusText(frameItem.workState)}
          </VStatusBadge>
        </VFlex>
      )}

      {isOwnedByOther && <Person className="cursor-pointer text-primary-500" />}

      {!isAssigned && !isApplicable && !isOwnedByOther && (
        <Person className="text-primary-500" />
      )}
    </VFlex>
  );
};

export const LinkedFrameList = ({
  excludedFrameSeqSet,
  frameList,
  onApply,
  onNavigate,
  onRemoveItem,
  onRemoveList,
}: LinkedFrameListProps) => {
  const user = useAuthStore(state => state.user);
  const { value: isExpanded, toggle: toggleExpand } = useFlag(false);
  const filteredFrameList = excludedFrameSeqSet
    ? frameList.filter(frame => !excludedFrameSeqSet.has(frame.frameSeq))
    : frameList;
  const frameListTitle =
    filteredFrameList.length === 1
      ? `${filteredFrameList[0].frameSeq}`
      : `${filteredFrameList[0]?.frameSeq} - ${filteredFrameList[filteredFrameList.length - 1]?.frameSeq}`;
  const hasAssignedFrame = filteredFrameList.some(
    frame => frame.workerID === user.id,
  );
  const hasChild = filteredFrameList.length > 1;
  const isApplicable = filteredFrameList.some(frame =>
    isAssignedFrame(frame as FrameData, user),
  );
  const isEmpty = filteredFrameList.length === 0;
  const isOnlyChild = filteredFrameList.length === 1;

  const handleNavigate = (frameItem: LinkedFrameItemData) => {
    onNavigate?.(frameItem);
  };

  const handleRemoveItem = (frameItem: LinkedFrameItemData) => {
    onRemoveItem?.(frameItem);
  };

  const handleRemoveList = () => {
    onRemoveList?.(filteredFrameList);
  };

  const handleToggleExpand = () => {
    if (!hasChild) return;

    toggleExpand();
  };

  if (isEmpty) return null;

  return (
    <VStack gap={0.25} className="w-full [&_svg]:size-4">
      <VFlex items="center" gap={0.25} className="w-full">
        {isOnlyChild ? (
          <LinkedFrameItem
            frameItem={filteredFrameList[0]}
            isApplicable={isApplicable}
            isAssigned={user.id === filteredFrameList[0].workerID}
            onApply={() => onApply?.([filteredFrameList[0]])}
            onNavigate={() => handleNavigate(filteredFrameList[0])}
            onRemove={() => handleRemoveItem(filteredFrameList[0])}
          />
        ) : (
          <>
            <VFlex
              items="center"
              gap={0.25}
              className="h-[26px] flex-1 cursor-pointer bg-white px-1 py-0.5"
              onClick={handleToggleExpand}
            >
              <div className="w-3">
                {hasChild &&
                  (isExpanded ? (
                    <KeyboardArrowUp className="size-3 text-primary-500" />
                  ) : (
                    <KeyboardArrowDown className="size-3 text-primary-500" />
                  ))}
              </div>
              <VFlex items="center" gap={0.25} className="flex-1">
                <VideoLabel
                  className={cn({
                    'text-primary-500': isApplicable,
                    'text-gray-400': !isApplicable,
                  })}
                />
                <span className="text-body-md text-black">
                  {frameListTitle}
                </span>
              </VFlex>
            </VFlex>

            {hasAssignedFrame && (
              <VFlex
                items="center"
                justify="between"
                gap={0.25}
                className="w-fit"
              >
                <VIconButton
                  Icon={Done}
                  size="sm"
                  variant="primary"
                  onClick={() => onApply?.(filteredFrameList)}
                />
                <VIconButton
                  Icon={Close}
                  size="sm"
                  variant="roundDanger"
                  onClick={handleRemoveList}
                />
              </VFlex>
            )}

            {!hasAssignedFrame && (
              <Person className="cursor-pointer text-primary-500" />
            )}
          </>
        )}
      </VFlex>

      {isExpanded && hasChild && (
        <VStack gap={0.25} className="w-full">
          {filteredFrameList.map(frameItem => {
            const isApplicable = isApplicableFrame(frameItem as FrameData);

            return (
              <LinkedFrameItem
                key={frameItem.frameSeq}
                frameItem={frameItem}
                isApplicable={isApplicable}
                isAssigned={user.id === frameItem.workerID}
                onApply={() => onApply?.([frameItem])}
                onNavigate={() => handleNavigate(frameItem)}
                onRemove={() => handleRemoveItem(frameItem)}
              />
            );
          })}
        </VStack>
      )}
    </VStack>
  );
};

LinkedFrameList.displayName = 'LinkedFrameListItem';
